import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AdminRoute, AccountRoute, VolunteerRoute } from "@/components/RouteGuards";
import Signup from "@/pages/Signup";
import Account from "@/pages/Account";
import VolunteerApply from "@/pages/VolunteerApply";
import VolunteerActivate from "@/pages/VolunteerActivate";
import VolunteerWorkspace from "@/pages/VolunteerWorkspace";
import { Toaster } from "@/components/ui/sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnthemPlayer from "@/components/AnthemPlayer";
import LaunchBanner from "@/components/LaunchBanner";
import Home from "@/pages/Home";
import ProjectsPage from "@/pages/ProjectsPage";
import StoryPage from "@/pages/StoryPage";
import AboutPage from "@/pages/AboutPage";
import FutureCampusPage from "@/pages/FutureCampusPage";
import TransparencyPage from "@/pages/TransparencyPage";
import ContactPage from "@/pages/ContactPage";
import GalleryPage from "@/pages/GalleryPage";
import MercyMadePage from "@/pages/MercyMadePage";
import TheNeedPage from "@/pages/TheNeedPage";
import UnderstandingEthiopiaPage from "@/pages/UnderstandingEthiopiaPage";
import FoundingNetworkPage from "@/pages/FoundingNetworkPage";
import CommunityImpactPage from "@/pages/CommunityImpactPage";
import ABEthiopiaPage from "@/pages/ABEthiopiaPage";
import FounderPage from "@/pages/FounderPage";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import { ShopProvider } from "@/context/ShopContext";

import Login from "@/pages/admin/Login";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import Dashboard from "@/pages/admin/Dashboard";

function PublicLayout({ children }) {
  return (
    <div className="noise-bg">
      <LaunchBanner />
      <Navbar />
      <main>{children}</main>
      <AnthemPlayer />
      <Footer />
    </div>
  );
}

function ScrollManager({ lenisRef }) {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const lenis = lenisRef.current;
    if (hash) {
      let tries = 0;
      const attempt = () => {
        const el = document.querySelector(hash);
        if (el) {
          if (lenis) lenis.scrollTo(el, { offset: -110, duration: 1 });
          else el.scrollIntoView({ behavior: "smooth" });
        } else if (tries++ < 10) {
          setTimeout(attempt, 60);
        }
      };
      setTimeout(attempt, 60);
    } else {
      if (lenis) lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    }
  }, [pathname, hash, lenisRef]);
  return null;
}

function App() {
  const lenisRef = useRef(null);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    function loop(t) { lenis.raf(t); raf = requestAnimationFrame(loop); }
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <ShopProvider>
        <BrowserRouter>
          <ScrollManager lenisRef={lenisRef} />
          <Toaster position="bottom-right" richColors />
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/projects" element={<PublicLayout><ProjectsPage /></PublicLayout>} />
            <Route path="/story" element={<PublicLayout><StoryPage /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
            <Route path="/future-campus" element={<PublicLayout><FutureCampusPage /></PublicLayout>} />
            <Route path="/transparency" element={<PublicLayout><TransparencyPage /></PublicLayout>} />
            <Route path="/gallery" element={<PublicLayout><GalleryPage /></PublicLayout>} />
            <Route path="/mercy-made" element={<PublicLayout><MercyMadePage /></PublicLayout>} />
            <Route path="/the-need" element={<PublicLayout><TheNeedPage /></PublicLayout>} />
            <Route path="/understanding-ethiopia" element={<PublicLayout><UnderstandingEthiopiaPage /></PublicLayout>} />
            <Route path="/join" element={<PublicLayout><FoundingNetworkPage /></PublicLayout>} />
            <Route path="/community-impact" element={<PublicLayout><CommunityImpactPage /></PublicLayout>} />
            <Route path="/a-better-ethiopia" element={<PublicLayout><ABEthiopiaPage /></PublicLayout>} />
            <Route path="/founder" element={<PublicLayout><FounderPage /></PublicLayout>} />
            <Route path="/shop" element={<PublicLayout><ShopPage /></PublicLayout>} />
            <Route path="/shop/product/:slug" element={<PublicLayout><ProductPage /></PublicLayout>} />
            <Route path="/cart" element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/account" element={<AccountRoute><Account /></AccountRoute>} />
            <Route path="/volunteer/apply" element={<PublicLayout><VolunteerApply /></PublicLayout>} />
            <Route path="/volunteer/activate" element={<VolunteerActivate />} />
            <Route path="/volunteer" element={<VolunteerRoute><VolunteerWorkspace /></VolunteerRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/*" element={<AdminRoute><Dashboard /></AdminRoute>} />
          </Routes>
        </BrowserRouter>
        </ShopProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
